import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Settings, RotateCcw, Save, Loader2 } from 'lucide-react';
import { CategoryToggles } from './CategoryToggles';
import { DimensionToggles } from './DimensionToggles';
import type {
  ISettingsPanelProps,
  IAIStudioPreferencesInput,
  SceneCategory,
  IEnabledDimensions,
} from '@/@types/ai-studio';
import { getDefaultPreferencesInput } from '@/@types/ai-studio';

interface ISettingsPanelInternalProps extends ISettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsPanel({
  preferences,
  onPreferencesChange,
  isSaving = false,
  disabled = false,
  isOpen,
  onClose,
}: ISettingsPanelInternalProps) {
  // Local state for unsaved changes.
  // `committedPreferences` tracks the last prop value we synced from, so we can detect
  // when the server sends a new value and reset local edits (if the user hasn't changed anything).
  const [committedPreferences, setCommittedPreferences] =
    useState<IAIStudioPreferencesInput>(preferences);
  const [localPreferences, setLocalPreferences] = useState<IAIStudioPreferencesInput>(
    () => preferences
  );
  const [hasChanges, setHasChanges] = useState(false);

  // When the preferences prop changes from the server and the user has no pending edits,
  // reset local state during render (React's derived-state pattern — no effect needed).
  if (!hasChanges && preferences !== committedPreferences) {
    setCommittedPreferences(preferences);
    setLocalPreferences(preferences);
  }

  const handleCategoriesChange = (categories: SceneCategory[]) => {
    setLocalPreferences(prev => ({ ...prev, enabledCategories: categories }));
    setHasChanges(true);
  };

  const handleDimensionsChange = (dimensions: IEnabledDimensions) => {
    setLocalPreferences(prev => ({ ...prev, enabledDimensions: dimensions }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onPreferencesChange(localPreferences);
    setHasChanges(false);
    onClose();
  };

  const handleReset = () => {
    const defaults = getDefaultPreferencesInput();
    setLocalPreferences(defaults);
    setHasChanges(true);
  };

  const handleClose = () => {
    // Reset local state to original preferences when closing without saving
    if (hasChanges) {
      setLocalPreferences(preferences);
      setHasChanges(false);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="size-5" />
            AI Studio Settings
          </DialogTitle>
          <DialogDescription>
            Customize which scene categories and dimensions the AI will use when generating images.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh]">
          <div className="flex flex-col gap-6 pr-4">
            {/* Scene Categories */}
            <CategoryToggles
              enabledCategories={localPreferences.enabledCategories}
              onCategoriesChange={handleCategoriesChange}
              disabled={disabled || isSaving}
            />

            <Separator />

            {/* Dimensions */}
            <DimensionToggles
              enabledDimensions={localPreferences.enabledDimensions}
              onDimensionsChange={handleDimensionsChange}
              disabled={disabled || isSaving}
            />
          </div>
        </ScrollArea>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={handleReset}
            disabled={disabled || isSaving}
          >
            <RotateCcw className="size-4" />
            Reset to Defaults
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={disabled || isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="size-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
